/**
 * EmployeePhotoUpload — profile-picture uploader for the employee form
 * (edit mode) + detail page. Two-phase direct-to-S3 (MinIO) upload, mirroring
 * the bills/attachments flow:
 *   1. `employees.requestPhotoUpload` → presigned PUT URL + object key
 *   2. `fetch(url, { method:'PUT', body:file })` straight to storage
 *   3. `employees.confirmPhoto` with the same key → flips photo_object_key
 *
 * Shows the current photo (or initials) + Upload / Remove. Type + size are
 * validated client-side (the server re-validates).
 */
import { useRef, useState } from "react";
import { Loader2, Trash2, Upload } from "lucide-react";
import { Alert, Button } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { EmployeeAvatar } from "./employee-avatar.tsx";

const ACCEPT = "image/jpeg,image/png,image/webp";
const ALLOWED = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_BYTES = 2 * 1024 * 1024;
type PhotoType = "image/jpeg" | "image/png" | "image/webp";

export function EmployeePhotoUpload({
  employeeId,
  name,
  initialUrl,
}: {
  employeeId: string;
  name: string;
  initialUrl: string | null;
}) {
  const utils = trpc.useUtils();
  const inputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState<string | null>(initialUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestUpload = trpc.employees.requestPhotoUpload.useMutation();
  const confirm = trpc.employees.confirmPhoto.useMutation();
  const remove = trpc.employees.removePhoto.useMutation();

  const invalidate = () =>
    Promise.all([
      utils.employees.get.invalidate({ id: employeeId }),
      utils.employees.list.invalidate(),
    ]);

  const onPick = async (file: File) => {
    setError(null);
    if (!ALLOWED.has(file.type)) {
      setError("Use a JPEG, PNG, or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Image exceeds the 2 MB limit.");
      return;
    }
    setBusy(true);
    try {
      const req = await requestUpload.mutateAsync({
        employeeId,
        contentType: file.type as PhotoType,
        size: file.size,
      });
      const put = await fetch(req.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status}).`);
      const res = await confirm.mutateAsync({ employeeId, objectKey: req.objectKey });
      setUrl(res.photoUrl);
      await invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setError(null);
    setBusy(true);
    try {
      await remove.mutateAsync({ employeeId });
      setUrl(null);
      await invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <EmployeeAvatar name={name} photoUrl={url} size="lg" />
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-ink">Profile photo</h3>
        <p className="mt-0.5 text-xs text-muted">
          Shown across payroll screens &amp; the payslip. JPEG/PNG/WebP, up to 2 MB.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            type="button"
            intent="ghost"
            size="sm"
            disabled={busy}
            className="border border-line-2"
            onClick={() => inputRef.current?.click()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {url ? "Change photo" : "Upload photo"}
          </Button>
          {url ? (
            <Button
              type="button"
              intent="ghost"
              size="sm"
              disabled={busy}
              className="text-bad hover:bg-bad-50"
              onClick={() => void onRemove()}
            >
              <Trash2 size={14} /> Remove
            </Button>
          ) : null}
        </div>
        {error ? (
          <div className="mt-2">
            <Alert intent="bad" description={error} />
          </div>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onPick(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
